import { Component, ElementRef, OnInit, ViewChild } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import { AdvancedSearchModel } from '@app/components/controls/utilities/models/advanced-search-model';
import { TextBoxModel } from '@app/components/controls/utilities/models/text-box-model';
import { BuilderService } from '@app/components/controls/utilities/services/builder.service';
import { ApiService } from '@app/components/utilities/services/api.service';
import { BaseService } from '@app/system/services/base.service';
import { DetectDeviceService } from '@app/system/services/detect-device.service';
import { GlobalFunctionService } from '@app/system/services/global-function.service';
import { LocaleService } from '@app/system/services/locale.service';
import { ApiService as RegistrationApiService } from '../utilities/services/api.service';
import { PopupComponent } from '@app/components/popup/popup.component';
import { MatLegacyDialog as MatDialog } from '@angular/material/legacy-dialog';
import { AutoCompleteModel } from '@app/components/controls/utilities/models/auto-complete-model';
import { AppInitializerService } from '@app/app-initializer.service';
import { GlobalApiService } from '@app/system/services/global-api.service';

@Component({
	selector: 'app-registration-workflow-forms',
	templateUrl: './registration-workflow-forms.component.html',
	styleUrls: ['./registration-workflow-forms.component.scss']
})
export class RegistrationWorkflowFormsComponent implements OnInit {

	@ViewChild('formElement') set setFocus(content: ElementRef) {
		if (this.action === 'requestRegistrationList' && content && this.detectDevice.resolutionState$.value.deviceType === 'desktop') {
			const el: HTMLElement = content.nativeElement.querySelector('#search');
			if (el) {
				const parentEl: HTMLElement = (el.firstChild as HTMLElement);
				parentEl.getElementsByTagName('input')[0].focus();
			}
		}
	}

	action: 'register' | 'requestRegistration' | 'requestRegistrationList' | 'addRegistration';
	title = '';
	info = '';
	loader = true;
	submitDisable = true;
	formFields: any = [];
	popupRef;
	formObject;
	formObjectData = {
		form: null,
		fields: []
	};
	record;
	config = { fxFlex: 100 };
	tabName;
	tabIndex = 0;
	search: AdvancedSearchModel;
	form;
	pageNumber = 1;
	limit = 10;
	requestCount = 0;
	requestList = [];
	clearTimeout;
	controlValue;
	buttonOptions: any;

	constructor(
		public locale: LocaleService,
		private baseService: BaseService,
		public detectDevice: DetectDeviceService,
		private popup: MatDialog,
		private controlBuild: BuilderService,
		public gfService: GlobalFunctionService,
		private apiService: ApiService,
		private registrationApiService: RegistrationApiService,
		private globalApiService: GlobalApiService,
		private appInit: AppInitializerService
	) { }

	ngOnInit(): void {
		this.baseService.formView = true;
		if (this.action === 'register') {
			this.title = this.locale.translate('register');
			const notes: TextBoxModel = {
				fieldColumn: 'notes',
				additionalMetaData: {
					maxChar: 250,
					fieldConfig: { appearance: 'standard' },
					showLabel: true
				},
				fieldType: 'textBox',
				isHidden: false,
				isReadonly: false,
				isRequired: false,
				type: 'text',
				validationRegex: null,
				fieldCaption: this.locale.translate('notes_if_any'),
				fieldHelpText: null,
				fieldOrder: 1,
				validationMessage: '',
				fieldPlaceholder: this.locale.translate('notes_if_any')
			};
			this.formFields = [
				notes
			];
			this.submitDisable = false;
			this.popupRef.componentInstance.popupLoader = false;
		} else if (this.action === 'requestRegistration') {
			if (this.record && this.record.resourceTypeID && this.record.resourceTypeID.key && this.record.resourceTypeID.key === 'internalEvent') {
				this.title = this.locale.translate('request_registration');
			} else {
				this.title = this.locale.translate('request_approval');
			}
			const userRemarks: TextBoxModel = {
				fieldColumn: 'userRemarks',
				additionalMetaData: {
					maxChar: 250,
					fieldConfig: { appearance: 'standard' },
					showLabel: true
				},
				fieldType: 'textBox',
				isHidden: false,
				isReadonly: false,
				isRequired: false,
				type: 'text',
				validationRegex: null,
				fieldCaption: this.locale.translate('notes_if_any'),
				fieldHelpText: null,
				fieldOrder: 1,
				validationMessage: '',
				fieldPlaceholder: this.locale.translate('notes_if_any')
			};
			this.formFields = [
				userRemarks
			];
			this.submitDisable = false;
			this.popupRef.componentInstance.popupLoader = false;
		} else if (this.action === 'requestRegistrationList') {
			if (!this.record.datePassed) {
				this.buttonOptions = { caption: this.locale.translate('register_users'), icon: 'add_circle' };
			}
			this.title = this.record.title;
			if (this.tabName === 'requests') {
				this.tabIndex = 1;
			}
			this.search = {
				additionalMetaData: {
					filter: []
				},
				category: null,
				fieldColumn: 'search',
				fieldType: 'advancedSearch',
				isHidden: false,
				isReadonly: false,
				fieldCaption: '',
				fieldHelpText: '',
				fieldHelpTextLong: '',
				fieldOrder: 1,
				fieldPlaceholder: 'Search',
				isRequired: false,
				options: [],
				selectedValue: [],
				type: 'classic'
			};
			this.formFields = [this.search];
			const data = this.controlBuild.formGroupBuilder(this.formFields);
			this.form = data;
			this.getRequest();
		} else if (this.action === 'addRegistration') {
			this.title = this.locale.translate('register_users');
			this.popupRef.componentInstance.popupLoader = true;
			const userID: AutoCompleteModel = {
				fieldColumn: 'userID',
				additionalMetaData: {
					showLabel: true,
					autoFill: 'off'
				},
				fieldType: 'autoComplete',
				isHidden: false,
				isMultiple: true,
				isReadonly: false,
				isRequired: true,
				options: [],
				selectedValue: [],
				fieldCaption: this.locale.translate('select_users'),
				fieldOrder: 1,
				preDefined: true,
				fieldPlaceholder: this.locale.translate('select_users')
			};

			this.formFields = [userID];
			this.popupRef.componentInstance.popupLoader = false;
			this.submitDisable = false;
		}
	}

	formObjectReader(record: any) {
		this.formObject = record;
		const form: UntypedFormGroup = record.form;
		let subscribe;
		if (this.action === 'addRegistration') {
			const userIDField = this.formFields.find((items) => items.fieldColumn === 'userID');

			form.controls.userID.valueChanges.subscribe((controlResp) => {
				if (navigator.onLine) {
					if (subscribe && !subscribe['closed']) {
						subscribe.unsubscribe();
					}
					if (this.clearTimeout) {
						clearTimeout(this.clearTimeout);
					}
					this.clearTimeout = setTimeout(() => {
						if (controlResp && typeof controlResp === 'string') {
							const apiData: any = {
								searchString: controlResp
							};
							const header: any = {};
							let selectedUser = [];
							if (userIDField.selectedValue.length) {
								userIDField.selectedValue.map(items => {
									if (items.isAdd && !items.isDelete) {
										selectedUser.push(items.key.userID);
									}
								});
							}

							subscribe = this.globalApiService.getWorkGroupUsers(apiData, header).subscribe(
								(response) => {
									if (response) {
										if (selectedUser.length) {
											response.records = response.records.filter(items => {
												if (!selectedUser.includes(items.userID)) {
													return items;
												}
											});
										}
										userIDField.options = response.records.map((data) => {
											return {
												key: data,
												value: `${data.firstName}${data.lastName
													? ' ' + data.lastName
													: ''}`,
												prefix: {
													type: 'image',
													value: data.profilePicURL
												},
												shortInfo: this.baseService.getUserShortInfo(data)
											};
										});
									} else {
										userIDField.options = [];
									}
								},
								(error) => {
									this.gfService.errorHandler(error, {
										snackBar: {
											position: 'before'
										}
									});
									userIDField.options = [];
								}
							);
						} else {
							userIDField.options = [];
						}
					}, this.appInit.configuration.searchDelay);
				}
			});

		}
	}

	submit() {
		if (navigator.onLine) {
			if (this.action === 'register' || this.action === 'addRegistration') {
				this.register();
			} else if (this.action === 'requestRegistration') {
				this.requestRegistration();
			}
		}
	}

	searchPeopleDetail(data) {
		if (data && data.userID) {
			this.baseService.userDetail(data.userID);
		}
	}

	searched() {
		if (navigator.onLine) {
			this.pageNumber = 1;
			this.requestCount = 0;
			this.loader = true;
			this.getRequest();
		}
	}

	clear() {
		if (navigator.onLine) {
			this.pageNumber = 1;
			this.requestCount = 0;
			this.loader = true;
			this.getRequest();
		}
	}

	pagination(pageEvent) {
		this.limit = pageEvent.limit;
		this.pageNumber = pageEvent.pageNumber;
		this.getRequest();
	}

	tabChange(index) {
		this.tabIndex = index;
		if (this.tabIndex === 0) {
			this.tabName = 'registration';
		} else if (this.tabIndex === 1) {
			this.tabName = 'requests';
		}
		this.requestList = [];
		this.limit = 10;
		this.pageNumber = 1;
		this.requestCount = 0;
		if (this.formObject) {
			this.formObject.form.controls.search.patchValue(null, { emitEvent: false });
		}
		this.getRequest();
	}

	getRequest() {
		this.loader = true;
		const headers = {
		};
		const apiData: any = {
			limit: this.limit,
			pageNumber: this.pageNumber,
			lanCode: sessionStorage.getItem('locale') || 'en',
			resourceID: this.record.resourceID
		};
		if (this.formObject && this.formObject.form.controls.search.value) {
			apiData.searchString = this.formObject.form.controls.search.value;
			this.controlValue = apiData.searchString;
		} else {
			this.controlValue = null;;
		}
		apiData.type = 1;

		let method = this.registrationApiService.getRegisterList(apiData, headers);
		if (this.tabIndex === 1) {
			method = this.registrationApiService.getRegisterRequestList(apiData, headers);
		}
		method
			.subscribe(
				(response) => {
					this.requestCount = 0;
					this.requestList = [];
					if (response && response.hasOwnProperty('records')) {
						this.dataBuild(response.records);
						this.requestCount = response.totalRecordCount;
					}
				},
				(error) => {
					this.gfService.errorHandler(error, {
						snackBar: {
							position: 'before'
						}
					});
					this.requestCount = 0;
					this.requestList = [];
				}
			)
			.add(() => {
				this.loader = false;
			});
	}

	dataBuild(records) {
		for (const data of records) {
			if ((this.tabName === 'registration' && [1, 2, 3].includes(data.status)) || (this.tabName === 'requests' && [0, 2].includes(data.status))) {
				const shortInfo = [];
				data.date = this.gfService.dateFormat(data.date, 'd MMM');
				data.name = `${data.user.firstName}${data.user.lastName
					? ' ' + data.user.lastName
					: ''}`;
				if (data.user.designation && data.user.designation.name) {
					shortInfo.push(
						{
							value: data.user.designation.name
						}
					);
				}
				if (data.user.department && data.user.department.name) {
					shortInfo.push(
						{
							value: data.user.department.name
						}
					);
				}
				if (data.user.city && data.user.city.name) {
					shortInfo.push(
						{
							value: data.user.city.name
						}
					);
				}
				data.shortInfo = shortInfo;

				if (this.tabName === 'registration') {
					if (data.status === 3) {
						data.requestStatus = this.locale.translate('dint_attend');
					} else if (data.status === 2) {
						data.requestStatus = this.locale.translate('attended');
					}
				} else if (this.tabName === 'requests') {
					if (data.status === 2) {
						data.requestStatus = this.locale.translate('declined_status');
					}
				}
				this.requestList.push(data);
			}

		}
	}

	updateRequest(action: string, data: any, additionalData: any = {}) {
		if (navigator.onLine) {
			data.load = true;
			const headers: any = {};
			const formData: any = {
			};
			let msg = 'request_accepted';
			data.deleting = true;
			if (action === 'approve') {
				formData.status = 1;
			} else if (action === 'decline') {
				formData.status = 2;
				msg = 'request_declined';
			}
			this.registrationApiService.updateRequest(formData, data.requestID, headers).subscribe(
				(response) => {
					data.load = false;
					data.deleting = false;
					if (action === 'approve') {
						this.requestList.splice(additionalData.index, 1);
						this.requestCount -= 1;
					} else if (action === 'decline') {
						data.status = 2;
						data.requestStatus = this.locale.translate('declined_status');
					}
					this.gfService.openSnackBar('SUCCESS', {
						message: this.locale.translate(msg),
						icon: 'check_circle_outline',
						position: 'before'
					});
				},
				(error) => {
					let errorMsg: string = error.error.message;
					if (error.error.hasOwnProperty('errorCode') && error.error.errorCode === "DUPLICATE") {
						errorMsg = this.locale.translate('you_have_already_registered');
					}
					this.gfService.openSnackBar('ERROR', {
						message: errorMsg,
						icon: 'error',
						position: 'before'
					});
					data.load = false;
					data.deleting = false;
				}
			).add(() => {
				if (this.popupRef && this.popupRef.componentInstance) {
					this.popupRef.componentInstance.dialogCloseData = { updated: true };
				}
			});
		}
	}

	addRegistration(event, action: string) {
		const dialogRef = this.popup.open(PopupComponent, {
			panelClass: ['custom-panel'],
			data: {
				action: 'dynamic',
				popup: true,
				component: RegistrationWorkflowFormsComponent,
				componentData: [
					{
						variableName: 'action',
						value: action
					},
					{
						variableName: 'record',
						value: this.record
					}
				],
				triggerEl: event
			},
			disableClose: true,
			maxWidth: '860px'
		});
		dialogRef.afterClosed().subscribe(async (result) => {
			if (result && typeof result === 'object') {
				if (action === 'addRegistration') {
					if (this.popupRef && this.popupRef.componentInstance) {
						this.popupRef.componentInstance.dialogCloseData = { updated: true };
					}
					this.tabChange(0);
				}
			}
		});
	}

	alertPopup(action: string, data: any, additionalData: any = {}) {
		const dialogRef = this.popup.open(PopupComponent, {
			panelClass: ['custom-panel'],
			autoFocus: false,
			data: {
				action: 'alert',
				classes: 'warning',
				popup: true,
				icon: 'warning',
				title: this.locale.translate('decline_request'),
				content: this.locale.translate('decline_request_text'),
				buttons: [
					{
						type: 'main-action',
						caption: this.locale.translate('yes'),
						key: 'yes'
					},
					{
						type: 'main-action',
						caption: this.locale.translate('no'),
						key: 'no',
						autoFocus: true
					}
				],
				triggerEl: additionalData.actionElement
			},
			disableClose: true,
			maxWidth: '560px'
		});
		dialogRef.afterClosed().subscribe((resp) => {
			if (resp) {
				if (resp.key === 'yes') {
					this.updateRequest(action, data, additionalData);
				}
			}
		});
	}

	register() {
		if (this.formObjectData && this.formObjectData.form) {
			this.submitDisable = true;
			const headers: any = {};
			this.gfService.alertClear('registrationWorkflowAlert');
			if (this.formObjectData.form.valid) {
				this.popupRef.componentInstance.popupLoader = true;
				let apiData = this.gfService.fieldSubmitApiValueConstruct(
					this.formObjectData.form.getRawValue(),
					this.formObjectData.fields
				);

				let userData = {};
				if (this.action === 'addRegistration') {
					const userIds = [];
					for (const data of apiData.userID) {
						if (data.isAdd && !data.isDelete) {
							userIds.push(data.key.userID);
							userData = data.key;
						}
					}
					apiData = {
						userID: userIds,
					};

				} else if (this.action === 'register') {
					apiData.info = {
						notes: apiData.notes
					};
					delete apiData.notes;
				}

				apiData.resourceID = this.record.resourceID;
				apiData.resourceTypeID = this.record.resourceTypeID.value;
				this.apiService.register(apiData, headers).subscribe(
					(response) => {
						this.baseService.logUserActivity('register', 'learning', [''], { resourceData: this.record });
						this.popupRef.close(this.gfService.JSONMerge(userData, response));
						this.gfService.openSnackBar('SUCCESS', {
							message: this.locale.translate('registered'),
							icon: 'check_circle_outline',
							position: 'before'
						});
					},
					(error) => {
						if (error.error.hasOwnProperty('errorCode') && error.error.errorCode === 'DUPLICATE') {
							error.error = this.locale.translate('user_already_registered');
						}
						this.gfService.errorHandler(error, {
							alert: {
								id: 'registrationWorkflowAlert'
							},
							form: this.formObjectData.form
						});
					}
				)
					.add(() => {
						this.submitDisable = false;
						this.popupRef.componentInstance.popupLoader = false;
					});
			} else {
				this.gfService.fieldControlMarkAsTouched(this.formObjectData.fields, this.formObjectData.form);
				this.submitDisable = false;
			}
		}
	}

	requestRegistration() {
		if (this.formObjectData && this.formObjectData.form) {
			this.submitDisable = true;
			const headers: any = {};
			this.gfService.alertClear('registrationWorkflowAlert');
			if (this.formObjectData.form.valid) {
				this.popupRef.componentInstance.popupLoader = true;
				const apiData = this.gfService.fieldSubmitApiValueConstruct(
					this.formObjectData.form.getRawValue(),
					this.formObjectData.fields
				);
				apiData.resourceID = this.record.resourceID;
				apiData.resourceTypeID = this.record.resourceTypeID.value;
				this.apiService.registerRequest(apiData, headers).subscribe(
					(response) => {
						this.baseService.logUserActivity('requestregistration', 'learning', [''], { resourceData: this.record });
						this.popupRef.close(this.gfService.JSONMerge(apiData, response));
						this.gfService.openSnackBar('SUCCESS', {
							message: this.locale.translate('request_has_been_sent'),
							icon: 'check_circle_outline',
							position: 'before'
						});
					},
					(error) => {
						this.gfService.errorHandler(error, {
							alert: {
								id: 'registrationWorkflowAlert'
							},
							form: this.formObjectData.form
						});
					}
				)
					.add(() => {
						this.submitDisable = false;
						this.popupRef.componentInstance.popupLoader = false;
					});
			} else {
				this.gfService.fieldControlMarkAsTouched(this.formObjectData.fields, this.formObjectData.form);
				this.submitDisable = false;
			}
		}
	}
}
